import { useCallback } from 'react';
import { Logger } from '@saviynt/common';

import {
  type RenderModeState,
  type SortState,
} from '../../features/ViolationsTab/data-layer/violationsTabTypes';

import { useTypedSelector } from './use-typedselector';

const PERSONLIZATION_KEY = 'aag-personlization';

interface PersonalizationObjectState {
  sort?: SortState;
  tab?: string | null;
  filters?: Record<string, string[][]>;
  entitlementFilters?: Record<string, string[][]>;
  columns?: string[];
  widgets?: string[];
  mode?: RenderModeState;
}

const usePersonalization = () => {
  const {
    tabInAction,
    activeFilters,
    sortInAction,
    activeColumns,
    widgets,
    renderMode,
  } = useTypedSelector((state) => state.aag.violationsTab.controls);

  const savePersonlization = useCallback(
    (
      config: Record<string, boolean> = {
        saveTab: false,
        saveFilter: false,
        saveSort: false,
        saveColumn: false,
        saveWidget: false,
        saveMode: false,
      }
    ) => {
      const debounceTimer = setTimeout(() => {
        const personlizationObject: PersonalizationObjectState = {};

        // Tabs
        if (config.saveTab && tabInAction !== null) {
          personlizationObject.tab = tabInAction;
        }

        // Filters
        if (config.saveFilter && activeFilters.length > 0) {
          const appliedFilters: Record<string, string[][]> = {};

          activeFilters.forEach((filter) => {
            appliedFilters[filter.value] = filter.options.map((option) => [
              option.value,
              option.label,
            ]);
          });

          personlizationObject.filters = appliedFilters;
        }

        // Sort
        if (config.saveSort) {
          personlizationObject.sort = sortInAction;
        }

        // Columns
        if (config.saveColumn) {
          personlizationObject.columns = activeColumns
            .filter((column) => column.isChecked)
            .map((column) => column.value);
        }

        // Widgets
        if (config.saveWidget && widgets.length > 0) {
          personlizationObject.widgets = widgets
            .filter((widget) => widget.isChecked)
            .map((widget) => widget.value);
        }

        // Mode
        if (config.saveMode) {
          personlizationObject.mode = renderMode;
        }

        const isAvailableToSave = Boolean(
          Object.keys(personlizationObject).length > 0
        );

        if (isAvailableToSave) {
          const jsonString = JSON.stringify(personlizationObject);
          const encodedString = btoa(encodeURIComponent(jsonString));

          localStorage.setItem(PERSONLIZATION_KEY, encodedString);

          return;
        }

        Logger.log('Nothing to save for personalization');
      }, 500);

      return () => clearTimeout(debounceTimer);
    },
    [
      tabInAction,
      activeFilters,
      sortInAction,
      activeColumns,
      widgets,
      renderMode,
    ]
  );

  const getPersonlization = useCallback((key = PERSONLIZATION_KEY) => {
    const encodedString = localStorage.getItem(key);

    if (!encodedString) {
      return null;
    }

    // CWE-755: decodeURIComponent/atob/JSON.parse can throw on malformed or untrusted input
    try {
      const jsonString = decodeURIComponent(atob(encodedString));

      return JSON.parse(jsonString) as PersonalizationObjectState;
    } catch (e) {
      Logger.error('Failed to decode personalization data', e);

      return null;
    }
  }, []);

  return { savePersonlization, getPersonlization };
};

// eslint-disable-next-line import/prefer-default-export
export { usePersonalization };
